import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/site/LocaleLink";
import { useLang } from "@/context/LanguageContext";
import { PARTNERS } from "@/data/copy";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";

// The strip fills its width rather than leaving empty cells, so the column
// count tracks how many manufacturers we actually list — two today, capped at
// four as more are added. Written out in full because Tailwind only keeps
// class names it can see as complete literals.
const COLUMNS = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
};

export const Partners = () => {
  const { t } = useLang();
  return (
    <section className="bg-white py-24 lg:py-28" data-testid="partners">
      <div className="container-x">
        <div className="mb-14 max-w-2xl">
          <Reveal>
            <p className="eyebrow mb-4 text-gold-ink">{t.partners.kicker}</p>
          </Reveal>
          <Reveal i={1}>
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-forest sm:text-5xl">
              {t.partners.title}
            </h2>
          </Reveal>
          <Reveal i={2}>
            <p className="mt-5 text-base leading-relaxed text-stone sm:text-lg">{t.partners.sub}</p>
          </Reveal>
        </div>

        {/* Strip layout, sized for logos. Each cell renders the manufacturer's
            wordmark as type until real logo files exist — drop an <img> in
            place of the <span> and the layout is already correct. Every cell
            links to the product list filtered to that manufacturer. */}
        <RevealGroup
          className={`grid gap-px overflow-hidden rounded-2xl border border-line bg-line ${COLUMNS[Math.min(PARTNERS.length, 4)]}`}
        >
          {PARTNERS.map((partner, i) => (
            <RevealItem key={partner.manufacturer}>
              <Link
                to={`/products?manufacturer=${encodeURIComponent(partner.manufacturer)}`}
                data-testid={`partner-${i}`}
                aria-label={`${partner.name} — ${t.partners.viewProducts}`}
                className="group flex h-32 flex-col items-center justify-center gap-1.5 bg-white px-6 transition-colors duration-500 hover:bg-cream"
              >
                <span className="text-center font-display text-lg font-bold text-stone transition-colors duration-500 group-hover:text-forest sm:text-xl">
                  {partner.name}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-stone/0 transition-colors duration-500 group-hover:text-gold-ink">
                  {t.partners.viewProducts}
                  <ArrowUpRight className="h-3.5 w-3.5 rtl-flip" />
                </span>
              </Link>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
};
